<script setup lang="ts">
/**
 * MarketBrowser — Market group tree (lazy-loaded), item list, search
 *
 * Events:
 *   select-item — When an item is clicked (typeID, typeName, categoryID)
 */
import { ref, onMounted } from 'vue';
import { useFitStore } from '../../stores/fitStore';
import TreeView from './TreeView.vue';
import type { TreeNode } from './TreeView.vue';
import type { MarketGroup } from '../../../shared/types';
import { iconUrl } from '../../composables/useApi';

const store = useFitStore();

const emit = defineEmits<{
  'select-item': [typeID: number, typeName: string];
}>();

const searchInput = ref('');
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

function handleSearch(): void {
  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    store.searchMarketItems(searchInput.value);
  }, 300);
}

// Convert MarketGroup[] to TreeNode[] for TreeView
function marketGroupsToNodes(groups: MarketGroup[]): TreeNode[] {
  return groups.map(g => ({
    id: g.marketGroupID,
    label: g.marketGroupName,
    hasChildren: g.hasChildren,
    children: g.children ? marketGroupsToNodes(g.children) : undefined,
  }));
}

// Lazy load children for a market group
async function loadTreeChildren(parentId: number): Promise<TreeNode[]> {
  const children = await store.loadMarketTreeChildren(parentId);
  return marketGroupsToNodes(children);
}

function handleTreeToggle(id: number): void {
  store.toggleMarketGroup(id);
}

function handleTreeSelect(id: number): void {
  store.selectMarketGroup(id);
}

function selectItem(typeID: number, typeName: string): void {
  emit('select-item', typeID, typeName);
}

onMounted(() => {
  store.loadMarketTreeRoots();
});
</script>

<template>
  <div class="market-browser">
    <!-- Search -->
    <div class="search-box">
      <svg class="search-icon" width="14" height="14" viewBox="0 0 14 14">
        <circle cx="5.5" cy="5.5" r="4.5" fill="none" stroke="currentColor" stroke-width="1.5" />
        <line x1="9" y1="9" x2="13" y2="13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      </svg>
      <input
        v-model="searchInput"
        type="text"
        placeholder="Search items..."
        class="search-input"
        @input="handleSearch"
      />
    </div>

    <!-- Market Tree -->
    <div class="market-tree-container">
      <TreeView
        :nodes="marketGroupsToNodes(store.marketTreeRoots)"
        :load-children="loadTreeChildren"
        :expanded="store.expandedMarketGroups"
        :selected-id="store.selectedMarketGroupID"
        @toggle="handleTreeToggle"
        @select="handleTreeSelect"
      />

      <div v-if="store.marketBrowserLoading && store.marketTreeRoots.length === 0" class="loading-msg">
        Loading market groups...
      </div>
    </div>

    <!-- Item List -->
    <div v-if="store.marketItems.length > 0" class="item-list">
      <div
        v-for="item in store.marketItems"
        :key="item.typeID"
        class="item-entry"
        draggable="true"
        @click="selectItem(item.typeID, item.typeName)"
        @dragstart="(e: DragEvent) => {
          e.dataTransfer?.setData('text/plain', JSON.stringify({
            typeID: item.typeID,
            typeName: item.typeName,
          }));
        }"
      >
        <img
          :src="iconUrl(item.typeID, 32)"
          :alt="item.typeName"
          class="item-icon"
          loading="lazy"
        />
        <div class="item-info">
          <span class="item-name">{{ item.typeName }}</span>
          <span class="item-meta">{{ item.groupName }}</span>
        </div>
        <span class="item-meta-badge" :class="metaClass(item.metaGroupID)">
          {{ item.metaGroupName }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
function metaClass(metaGroupID: number): string {
  switch (metaGroupID) {
    case 1: return 'meta-t1';
    case 2: return 'meta-t2';
    case 4: return 'meta-faction';
    case 5: return 'meta-officer';
    case 6: return 'meta-deadspace';
    case 14: return 'meta-t2';
    default: return 'meta-t1';
  }
}
</script>

<style scoped>
.market-browser {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* Search */
.search-box {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--border-color);
}

.search-icon {
  color: var(--text-secondary);
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  color: var(--text-primary);
  font-size: 0.85rem;
  outline: none;
}

.search-input::placeholder {
  color: var(--text-secondary);
  opacity: 0.6;
}

/* Market Tree */
.market-tree-container {
  overflow-y: auto;
  max-height: 50%;
  min-height: 80px;
  border-bottom: 1px solid var(--border-color);
}

/* Item List */
.item-list {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.item-entry {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  cursor: pointer;
  transition: background var(--transition-fast);
  border-bottom: 1px solid rgba(42, 63, 95, 0.3);
}

.item-entry:hover {
  background: var(--bg-hover);
}

.item-entry[draggable="true"] {
  cursor: grab;
}

.item-entry[draggable="true"]:active {
  cursor: grabbing;
}

.item-icon {
  width: 28px;
  height: 28px;
  border-radius: var(--radius-sm);
  object-fit: contain;
  flex-shrink: 0;
}

.item-info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  flex: 1;
}

.item-name {
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-meta {
  font-size: 0.72rem;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Meta badges */
.item-meta-badge {
  font-size: 0.65rem;
  padding: 1px 5px;
  border-radius: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

.meta-t1 {
  background: rgba(158, 158, 158, 0.2);
  color: #bbb;
}

.meta-t2 {
  background: rgba(74, 158, 255, 0.2);
  color: var(--accent-blue);
}

.meta-faction {
  background: rgba(76, 175, 80, 0.2);
  color: var(--accent-green);
}

.meta-officer {
  background: rgba(156, 39, 176, 0.2);
  color: var(--accent-purple);
}

.meta-deadspace {
  background: rgba(255, 152, 0, 0.2);
  color: var(--accent-orange);
}

/* Messages */
.loading-msg {
  padding: 16px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.85rem;
}
</style>
